/* Seções do bloco "resumo global" do dashboard admin: alertas, visão
   financeira do mês, tendência de 6 meses e atividade recente. Dados
   vêm de GET /admin/dashboard/resumo (ver useAdminDashboardViewModel) -
   métricas que não fazem sentido escopadas a um evento, diferente do
   bloco de métricas por-evento que já existe em adminDashboardView. */

function tempoRelativo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h`;
  return `${Math.round(h / 24)} d`;
}

const MESES = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

/* Pequeno anel de progresso para a taxa de confirmação - composto
   dentro de um HueMetricCard existente via a prop `visual`. */
export function ConfirmacaoRing({ percentual }) {
  const raio = 17;
  const circunferencia = 2 * Math.PI * raio;
  const offset = circunferencia * (1 - percentual);
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
      <circle
        cx="22"
        cy="22"
        r={raio}
        fill="none"
        stroke="currentColor"
        strokeWidth="5"
        opacity="0.18"
      />
      <circle
        cx="22"
        cy="22"
        r={raio}
        fill="none"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray={circunferencia}
        strokeDashoffset={offset}
        transform="rotate(-90 22 22)"
      />
    </svg>
  );
}

function AlertaStrip({ alertas }) {
  if (!alertas) return null;

  const chips = [];
  const comprovantes = alertas.comprovantesPendentesAntigos;
  if (comprovantes?.quantidade > 0) {
    chips.push({
      key: 'comprovantes',
      texto: `${comprovantes.quantidade} comprovante${comprovantes.quantidade > 1 ? 's' : ''} aguardando há +${comprovantes.diasMinimo} dias`,
      bgClass: 'bg-h-damasco-wash',
      dotClass: 'bg-h-damasco-fill',
    });
  }
  const cobrancas = alertas.cobrancasVencendoHoje;
  if (cobrancas?.quantidade > 0) {
    chips.push({
      key: 'cobrancas',
      texto: `${cobrancas.quantidade} cobrança${cobrancas.quantidade > 1 ? 's' : ''} vencendo hoje`,
      bgClass: 'bg-h-vinho-wash',
      dotClass: 'bg-h-vinho-fill',
    });
  }
  const evento = alertas.proximoEventoBaixaConfirmacao;
  if (evento) {
    chips.push({
      key: 'evento',
      texto: `${evento.titulo}: ${Math.round(evento.taxaConfirmacao * 100)}% confirmado, falta${evento.diasRestantes > 1 ? 'm' : ''} ${evento.diasRestantes} dia${evento.diasRestantes > 1 ? 's' : ''}`,
      bgClass: 'bg-h-rosa-wash',
      dotClass: 'bg-h-rosa-fill',
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="mb-5">
      <div className="text-[11px] font-black uppercase tracking-wider text-graphite mb-2">
        Precisa de atenção
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        {chips.map((c) => (
          <div
            key={c.key}
            className={`flex-1 rounded-xl px-4 py-3 flex items-center gap-2.5 ${c.bgClass}`}
          >
            <span
              className={`w-2 h-2 rounded-full flex-shrink-0 ${c.dotClass}`}
            />
            <span className="text-[13px] text-ink">{c.texto}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Visão financeira GLOBAL do mês (MRR, receita do mês, a receber) -
   diferente das métricas por-evento que já existem acima na view
   principal (aquelas são escopadas ao evento selecionado no dropdown). */
function FinanceiroResumo({ financeiro, formatarReais, HueMetricCard }) {
  if (!financeiro) return null;

  const anterior = financeiro.receitaConfirmadaMesAnteriorCentavos;
  const delta =
    anterior > 0
      ? Math.round(
          ((financeiro.receitaConfirmadaMesCentavos - anterior) / anterior) *
            100,
        )
      : null;

  return (
    <div className="mb-5">
      <div className="text-[11px] font-black uppercase tracking-wider text-graphite mb-2">
        Visão financeira do mês
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <HueMetricCard
          label="Receita confirmada"
          value={formatarReais(financeiro.receitaConfirmadaMesCentavos)}
          sub={
            delta !== null
              ? `${delta >= 0 ? '▲' : '▼'} ${Math.abs(delta)}% vs. mês anterior`
              : 'Mês atual'
          }
          bgClass="bg-h-rosa-wash"
          labelClass="text-h-rosa-text"
          valueClass="text-h-rosa-display"
        />
        <HueMetricCard
          label="MRR · Lily Member"
          value={formatarReais(financeiro.mrrLilyMemberCentavos)}
          sub={`${financeiro.assinantesAtivos} ativa${financeiro.assinantesAtivos === 1 ? '' : 's'}${financeiro.assinantesEmAtraso > 0 ? ` · ${financeiro.assinantesEmAtraso} em atraso` : ''}`}
          bgClass="bg-h-malva-wash"
          labelClass="text-h-malva-text"
          valueClass="text-h-malva-display"
        />
        <HueMetricCard
          label="A receber"
          value={formatarReais(financeiro.aReceberCentavos)}
          sub={`${financeiro.aReceberQuantidade} pendente${financeiro.aReceberQuantidade === 1 ? '' : 's'}`}
          bgClass="bg-h-damasco-wash"
          labelClass="text-h-damasco-text"
          valueClass="text-h-damasco-display"
        />
      </div>
    </div>
  );
}

function TendenciaChart({ dados, formatarReais }) {
  if (!dados || dados.length === 0) {
    return (
      <div className="text-[13px] text-graphite">
        Sem dados suficientes ainda.
      </div>
    );
  }

  const valores = dados.map((d) => d.receitaConfirmadaCentavos);
  const max = Math.max(...valores, 1);
  const min = Math.min(...valores, 0);
  const range = max - min || 1;
  // Faixa vertical comprimida (25 a 95, nao 10 a 85) pra sobrar espaco pro
  // rotulo de valor acima/abaixo de cada ponto sem estourar o viewBox.
  const pontos = dados.map((d, i) => ({
    x: 12 + (i * 276) / (dados.length - 1 || 1),
    y: 95 - ((d.receitaConfirmadaCentavos - min) / range) * 70,
    valor: d.receitaConfirmadaCentavos,
  }));
  const linha = pontos
    .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ');

  // O SVG estica na horizontal para ocupar o card, o que é certo para a
  // linha mas deformava texto e pontos. Por isso só a linha fica no SVG;
  // pontos e rótulos são HTML posicionados em porcentagem sobre ele.
  return (
    <div>
      <div
        className="relative h-[140px]"
        role="img"
        aria-label="Tendência de receita confirmada nos últimos 6 meses"
      >
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 300 140"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <line
            x1="0"
            y1="95"
            x2="300"
            y2="95"
            stroke="#efeded"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
          <polyline
            points={linha}
            fill="none"
            stroke="#f64a8a"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        {pontos.map((p, i) => {
          const ultimo = i === pontos.length - 1;
          // Zigue-zague (rótulo acima em índices pares, abaixo em ímpares)
          // evita que 6 rótulos lado a lado se sobreponham num card estreito.
          const labelAcima = i % 2 === 0;
          const left = `${(p.x / 300) * 100}%`;
          const top = `${(p.y / 140) * 100}%`;
          const ancoraX = i === 0 ? '0%' : ultimo ? '-100%' : '-50%';
          return (
            <div key={i}>
              <span
                className="absolute rounded-full bg-[#f64a8a] -translate-x-1/2 -translate-y-1/2"
                style={{
                  left,
                  top,
                  width: ultimo ? 9 : 7,
                  height: ultimo ? 9 : 7,
                }}
              />
              <span
                className="absolute text-[11px] font-bold text-graphite whitespace-nowrap tabular-nums"
                style={{
                  left,
                  top,
                  transform: `translate(${ancoraX}, ${labelAcima ? 'calc(-100% - 8px)' : '8px'})`,
                }}
              >
                {formatarReais(p.valor)}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-mauve mt-0.5">
        {dados.map((d) => (
          <span key={d.mes}>{MESES[Number(d.mes.split('-')[1]) - 1]}</span>
        ))}
      </div>
    </div>
  );
}

function AtividadeRecente({ itens }) {
  if (!itens || itens.length === 0) {
    return (
      <div className="text-[13px] text-graphite">
        Nenhuma atividade recente.
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2.5">
      {itens.map((a, i) => (
        <div
          key={i}
          className="flex items-center justify-between text-[13px] gap-2"
        >
          <span className="text-ink">{a.descricao}</span>
          <span className="text-mauve text-xs flex-shrink-0">
            {tempoRelativo(a.data)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* Composição completa do bloco de resumo global. `HueMetricCard` é
   injetado pela view principal (mesmo componente já usado nas
   métricas por-evento) para não duplicar sua definição. */
export function ResumoDashboard({
  resumo,
  carregando,
  erro,
  formatarReais,
  HueMetricCard,
}) {
  if (erro) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-xs text-red-600 mb-5">
        {erro}
      </div>
    );
  }
  if (carregando || !resumo) return null;

  return (
    <>
      <AlertaStrip alertas={resumo.alertas} />
      <FinanceiroResumo
        financeiro={resumo.financeiro}
        formatarReais={formatarReais}
        HueMetricCard={HueMetricCard}
      />
      <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-4 mb-5">
        <div className="bg-white rounded-2xl border border-parchment p-5 sm:p-6">
          <div className="text-[11px] font-black uppercase tracking-wider text-graphite mb-3">
            Receita confirmada · últimos 6 meses
          </div>
          <TendenciaChart
            dados={resumo.tendenciaReceita}
            formatarReais={formatarReais}
          />
        </div>
        <div className="bg-white rounded-2xl border border-parchment p-5 sm:p-6">
          <div className="text-[11px] font-black uppercase tracking-wider text-graphite mb-3">
            Atividade recente
          </div>
          <AtividadeRecente itens={resumo.atividadeRecente} />
        </div>
      </div>
    </>
  );
}
